import FormResponse from './FormResponse';
import { createError } from '../error';

class FormResponseSet {
  constructor(data) {
    data = data || {};

    this.form = data.form;
    if (!this.form) {
      throw createError({
        name: "NoFormProvided",
        message: "Cannot create FormResponseSet without form",
      });
    }

    this.user_role_id = data.user_role_id || null;
    this.current_location = data.current_location || null;
    this.events_custom_responses_id = data.events_custom_responses_id || null;
    this.form_responses = data.form_responses || [];
    this.events_custom_forms_id = this.form.id;
    this.recapResponse = data.recapResponse || null;
    this.recapDefinitionId = data.recapDefinitionId || null;
    this.eventTimezoneName = data.eventTimezoneName || null;
    // One Additional Comments per form, kept with the recap response rather than on a capture.
    this.comment = data.comment != null ? String(data.comment) : '';
  }

  get hasComment() {
    return !!(this.form && this.form.isSamplingForm);
  }

  get hasOutOfStock() {
    return this.form_responses.some(r => r.out_of_stock && r.canMarkOutOfStock);
  }

  get commentMissing() {
    return this.hasOutOfStock && !String(this.comment || '').trim();
  }

  /**
   * Public properties
   */
  get canCreateResponse() {
    if (!this.form.isRepeatableForm || this.form.isProductForm) {
      return false;
    }
    if (this.form.isSamplingForm) {
      return !!this.form._merchandisingAllowAdditional && this._hasAddableProduct();
    }
    return true;
  }

  _hasAddableProduct() {
    const products = (Array.isArray(this.form.products) ? this.form.products : [])
      .filter(p => p && Number.isFinite(Number(p.id)) && Number(p.id) > 0);
    if (!products.length) {
      return true;
    }
    const picked = new Set();
    let awaitingChoice = false;
    for (const response of this.form_responses) {
      const id = response.product_labels_id != null ? Number(response.product_labels_id) : null;
      if (id) {
        picked.add(id);
      }
      if (response.merchProductMissing) {
        awaitingChoice = true;
      }
    }
    if (awaitingChoice) {
      return false;
    }
    return products.some(p => !picked.has(Number(p.id)));
  }

  set errors(errors) {
    this._errors = errors;
  }

  get errors() {
    return this._errors;
  }

  get hasErrors() {
    return this.validate().length > 0;
  }

  get visible() {
    let formVisible;
    if (this.form.logic_groups.length > 0) {
      formVisible = this.form.logic_groups.some(
        logicGroup => logicGroup.logicResultForRecapResponse(this.recapResponse)
      );
    } else {
      // No logic groups => always visible
      formVisible = true;
    }

    // Compare location variables
    if (formVisible) {
      const formLocationVariables = this.form.location_variables;
      const eventLocationVariables = this.recapResponse.eventLocationVariables;
      if (formLocationVariables && formLocationVariables.length) {
        const formLVIDs = formLocationVariables.map(lv => parseInt(lv.id));
        const eventLVIDs = eventLocationVariables.map(lv => parseInt(lv.id));
        formVisible = formLVIDs.some(id => eventLVIDs.indexOf(id) !== -1);
      }
    }

    // Compare role variables
    if (formVisible) {
      const formRoleVariables = this.form.role_variables;
      if (formRoleVariables && formRoleVariables.length) {
        const formRVIDs = formRoleVariables.map(rv => parseInt(rv.role_variables_id));
        formVisible = formRVIDs.indexOf(parseInt(this.user_role_id)) > -1;
      }
    }

    // Compare locations
    if (formVisible) {
      const formLocations = this.form.locations;
      if (formLocations && formLocations.length && this.current_location) {
        const formLocIDs = formLocations.map(loc => parseInt(loc.location_id));
        formVisible = formLocIDs.indexOf(parseInt(this.current_location.id)) > -1;
      }
    }

    // Compare location filters
    if (formVisible) {
      const logicGroups = this.form.logic_groups;
      if (logicGroups && logicGroups.length) {
        const results = [];
        logicGroups.forEach(group =>
          group.filters.forEach(filter => {
            const location = this.current_location;
            if (location) {
              const isNumber = filter.type === 'zip' || filter.type === 'chain' || filter.type === 'market'
              let compareValue = null;
              if (filter.type === 'zip' || filter.type === 'city' || filter.type === 'state' || filter.type === 'market') {
                compareValue = location[filter.type];
              } else if (filter.type === 'chain') {
                if (filter.subtype && filter.subtype === 'child chain') {
                  compareValue = location['chains_id'];
                } else {
                  compareValue = location['parent_chains_id'];
                }
              }
              if (compareValue) {
                if (isNumber) {
                  results.push(+compareValue === +filter.value);
                } else {
                  results.push(compareValue === filter.value);
                }
              } else {
                results.push(false);
              }
            } else {
              results.push(false);
            }
          })
        )
        formVisible = results.every(result => !!result);
      }
    }

    // Hide form if all form responses are hidden. Exception: a merchandising
    // product form with allow_additional + a non-empty pool stays visible so
    // the recapper can use the picker to add their first response - otherwise
    // an activity with no pre-selected products would hide the form entirely
    // and there'd be no UI to add anything.
    if (formVisible) {
      formVisible = this.collectVisibleFormResponses().length > 0;
      if (!formVisible
        && this.form
        && this.form.isProductScopeForm
        && this.form_responses.length > 0
      ) {
        formVisible = true;
      }
      if (!formVisible
        && this.form
        && this.form.isProductForm
        && this.form._merchandisingAllowAdditional
        && Array.isArray(this.form._merchandisingAdditionalScope)
        && this.form._merchandisingAdditionalScope.length > 0
      ) {
        formVisible = true;
      }
    }

    return formVisible;
  }

  set recapResponse(recapResponse) {
    this._recapResponse = recapResponse;
    // Persist to children
    for (let formResponse of this.form_responses) {
      formResponse.recapResponse = recapResponse;
    }
  }

  get recapResponse() {
    return this._recapResponse;
  }

  /**
   * Retrieve or create array of form responses.
   * For a product form, this will ensure a response is created for each product.
   */
  getOrCreateFormResponses() {
    if (!this.form_responses.length) {
      const responses = this._populateFormResponses();
      if (responses.length) {
        this.form_responses = responses;
      }
    }

    // For product forms, the visible response list is derived from
    // form.products (the canonical product list for the current mode):
    //   - Use Legacy Products = Yes -> form.products is whatever the recap
    //     definition had (legacy products-table flow).
    //   - Use Legacy Products = No  -> form.products is the merchandising
    //     scope overlay applied at load time.
    // Server-loaded responses arrive with product_labels_id / products_id but
    // no nested product object, so we attach the matching product here so
    // productName resolves. Responses whose product is not in form.products
    // (e.g. merchandising-mode rows surviving into legacy mode) are excluded
    // from the rendered list so toggling the mode doesn't render stale rows.
    // Saved data is not deleted - it is just not surfaced in the current mode.
    if (this.form && this.form.isProductForm) {
      // Always reference form.products directly so promotions from the
      // merchandising "additional" pool (below) persist across re-renders.
      // The earlier `... ? this.form.products : []` form created a fresh
      // local array when form.products was empty, so the promoted entries
      // were lost as soon as the loop's `if (response.product) continue;`
      // started skipping the now-attached responses on subsequent calls.
      if (!Array.isArray(this.form.products)) {
        this.form.products = [];
      }
      const products = this.form.products;
      // The merchandising scope's "additional" pool holds previously-picked
      // merchandising products that are not in the effective list yet; if a
      // saved response matches one, promote that product into form.products
      // so it renders as a regular row (and is removed from the picker pool
      // automatically because it is now in form.products).
      const additional = (this.form._merchandisingAdditionalScope && this.form._merchandisingAdditionalScope.length)
        ? this.form._merchandisingAdditionalScope
        : [];
      for (const response of this.form_responses) {
        if (response.product) continue;
        if (additional.length) {
          const additionalMatch = additional.find(p => p._merchandising
            ? response.product_labels_id === p.id
            : response.products_id === p.id);
          if (additionalMatch && products.indexOf(additionalMatch) === -1) {
            products.push(additionalMatch);
            response.product = additionalMatch;
            continue;
          }
        }
        const match = products.find(p => p._merchandising
          ? response.product_labels_id === p.id
          : response.products_id === p.id);
        if (match) response.product = match;
      }

      const visible = [];
      for (const product of products) {
        // A legacy product shows only to its audience, as getFormResponseForProduct() decides;
        // merchandising rows have none.
        if (!product._merchandising && !this.isProductShown(product)) {
          continue;
        }
        let response = this.form_responses.find(r => product._merchandising
          ? r.product_labels_id === product.id
          : r.products_id === product.id);
        if (!response) {
          response = this._createFormResponse({product});
          this.form_responses.push(response);
        } else if (!response.product) {
          response.product = product;
        }
        visible.push(response);
      }
      return visible;
    }

    return this.form_responses;
  }

  /**
   * Ensure a merchandising form has one instance per event-selected product
   * (default products), even when an existing/partial response is loaded.
   * Idempotent - matches on product_labels_id so a product that already has an
   * instance (saved or user-added) is never duplicated. Falls back to a single
   * empty instance only when there are no default products at all.
   *
   * Called push-based from the scope overlay (MerchandisingRecapScopeService
   * .applyOverlay) right after the form's _merchandisingDefaultProducts are
   * set, so it runs exactly once per load against a fresh response set - never
   * lazily (which previously created a stray empty instance before the scope
   * had been applied, and risked seeding stale defaults from the cached recap
   * definition).
   *
   * @returns {void}
   */
  ensureMerchandisingDefaultInstances() {
    if (!this.form || !this.form.isProductScopeForm) {
      return;
    }
    const defaults = Array.isArray(this.form._merchandisingDefaultProducts)
      ? this.form._merchandisingDefaultProducts
      : [];
    for (const product of defaults) {
      const exists = this.form_responses.some(
        r => r.product_labels_id != null && Number(r.product_labels_id) === Number(product.id)
      );
      if (!exists) {
        this.form_responses.push(this._createFormResponse({ product }));
      }
    }
    if (!this.form_responses.length) {
      const starter = this._createFormResponse();
      starter._manuallyAdded = true;
      this.form_responses.push(starter);
    }
  }

  createResponse() {
    let response = null;
    if (this.canCreateResponse) {
      response = this._createFormResponse();
      response._manuallyAdded = true;
      this.form_responses.push(response);
    }
    return response;
  }

  /**
   * Append a merchandising product (a row from form._merchandisingAdditionalScope)
   * to this form. Adds the product to form.products so getProductWithId can find
   * it on save round-trip, drops it from the form's _merchandisingAdditionalScope
   * pool so it can't be added twice, and creates a new FormResponse for it.
   *
   * No-op for non-merchandising products or when the form's allow-additional
   * flag is off.
   *
   * @param {object} product - synthetic merchandising row (must carry _merchandising)
   * @returns {FormResponse|null}
   */
  addMerchandisingResponse(product) {
    if (!this.form.isProductForm) return null;
    if (!product || !product._merchandising) return null;
    if (!this.form._merchandisingAllowAdditional) return null;

    // Idempotency: don't double-add for the same product_labels_id.
    const already = this.form_responses.find(r => r.product_labels_id === product.id);
    if (already) return already;

    this.form.products.push(product);

    const additional = this.form._merchandisingAdditionalScope || [];
    const idx = additional.findIndex(p => p.id === product.id);
    if (idx !== -1) {
      additional.splice(idx, 1);
    }

    const response = this._createFormResponse({product});
    // Tag the new response so the panel can offer a Remove button only on
    // recapper-added products (auto-loaded products from the original event
    // setup never carry this flag).
    response._manuallyAdded = true;
    this.form_responses.push(response);
    return response;
  }

  removeResponse(formResponse) {
    const idx = this.form_responses.indexOf(formResponse);
    if (idx === -1) return 0;

    // Inverse of addMerchandisingResponse: when undoing a manually-added
    // merchandising product, return it to the picker pool and pull it back
    // out of form.products so save round-trips don't resurrect the row.
    if (
      formResponse._manuallyAdded
      && formResponse.product
      && formResponse.product._merchandising
      && this.form.isProductForm
    ) {
      const product = formResponse.product;
      const additional = this.form._merchandisingAdditionalScope;
      if (Array.isArray(additional) && !additional.find(p => p.id === product.id)) {
        additional.push(product);
      }
      const productsIdx = this.form.products.indexOf(product);
      if (productsIdx !== -1) {
        this.form.products.splice(productsIdx, 1);
      }
    }

    this.form_responses.splice(idx, 1);
    return 1;
  }

  /**
   * Get the form response for the specified product, or create a new form response for that product
   * Each product (that matches the location variable filters) generates a separate form response.
   *
   * Here we need to check the location variables to determine if this product matches the event's LVs
   * so we can choose to display the form for that product.
   *
   * @param product
   * @return form response for this product
   */
  getFormResponseForProduct(product) {
    if (this.isProductShown(product)) {
      // Merchandising rows match by product_labels_id; legacy rows by products_id.
      let formResponse = this.form_responses.find(response => {
        if (product._merchandising) {
          return response.product_labels_id === product.id;
        }
        return response.products_id === product.id;
      });
      if (!formResponse) {
        formResponse = this._createFormResponse({product});
        this.form_responses.push(formResponse);
      } else if (!formResponse.product) {
        formResponse.product = product;
      }
      return formResponse;
    }

    // If there's no match then there's no form
    return null;
  }

  /**
   * Whether a product of this product form shows to this user at this event, by the audience the
   * builder's product settings give it: its location variables (one of the event venue's) and its
   * roles (the user's). Its venue assignments (locations) are not applied: venue-based targeting is
   * not supported in recap data entry. Merchandising scope rows carry none, so they always show.
   *
   * @param product
   * @return {boolean}
   */
  isProductShown(product) {
    const locationVariables = product.location_variables || [];
    const roleVariables = product.role_variables || [];

    // Location variables: shown where the event's venue has one of them, or where it has none.
    const eventLocationVariableIds = this.recapResponse.eventLocationVariables.map(lv => parseInt(lv.id));
    const displayLocationVariables = !locationVariables.length
      || locationVariables.some(lv => eventLocationVariableIds.indexOf(parseInt(lv.id)) > -1);

    // Roles: numbers on both sides, as the form and field checks compare them, whatever types the JSON carries.
    const productRoleVariableIds = roleVariables.map(rv => parseInt(rv.role_variables_id));
    const displayRoleVariables = !productRoleVariableIds.length
      || productRoleVariableIds.indexOf(parseInt(this.user_role_id)) > -1;

    return displayLocationVariables && displayRoleVariables;
  }

  collectValues() {
    return this.form_responses.map(formResponse => formResponse.collectValues());
  }

  validate() {
    this.errors = this.form_responses
      .map(formResponse => formResponse.validate())
      .reduce((pre, cur) => pre.concat(cur), []);
    if (this.commentMissing) {
      this.errors.push({ key: 'ADDITIONAL_COMMENTS_REQUIRED', message: 'Additional comments are required' });
    }
    return this.errors;
  }

  collectVisibleFormResponses() {
    return this.getOrCreateFormResponses().filter(formResponse => formResponse.visible);
  }

  _populateFormResponses() {
    if (this.form.isSimpleForm) {
      // Ensure first response exists for simple forms
      if (!this.form_responses.length) {
        this.form_responses.push(this._createFormResponse());
      }
    } else {
      if (this.form.isProductForm) {
        // Ensure responses exist for each product of product form
        const responses = this.form.products.map(product => this.getFormResponseForProduct(product));

        // Product responses that don't match any location variables will return null so check for that.
        for (const response of responses) {
          if (response && this.form_responses.indexOf(response) === -1) {
            this.form_responses.push(response);
          }
        }
      } else if (this.form.isProductScopeForm) {
        // Handled in ensureMerchandisingDefaultInstances
      } else {
        // Repeatable form; ensure at least one response exists
        if (!this.form_responses.length) {
          this.form_responses.push(this._createFormResponse());
        }
      }
    }
    return this.form_responses;
  }

  _createFormResponse(data) {
    data = {
      events_custom_responses_id: this.events_custom_responses_id,
      form: this.form,
      recapResponse: this.recapResponse,
      formResponseSet: this,
      user_role_id: this.user_role_id,
      current_location: this.current_location,
      ...data,
    };
    return new FormResponse(data);
  }

  updateDisplayLogic(changedFieldResponses) {
    const results = this.form_responses.map(formResponse => formResponse.updateDisplayLogic(changedFieldResponses));
    return this.visible && results.some(r => r);
  }

  updateLogicModifiers() {
    for (const formResponse of this.form_responses) {
      formResponse.updateLogicModifiers();
    }
  }
}

export default FormResponseSet;
